import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Briefcase, Building2, Users, Handshake, ArrowRight } from "lucide-react";
import Breadcrumb from "@/components/Breadcrumb";
import JobSearch from "@/components/jobs/JobSearch";
import JobCard from "@/components/jobs/JobCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function paramsToFilters(sp) {
  return {
    q: sp.get("q") || "",
    location: sp.get("location") || "all",
    department: sp.get("department") || "all",
    experience: sp.get("experience") || "all",
  };
}

export default function CareersPage() {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const [filters, setFilters] = useState(paramsToFilters(sp));
  const [jobs, setJobs] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => { setFilters(paramsToFilters(sp)); }, [sp]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.q.trim()) params.set("q", filters.q.trim());
    if (filters.location !== "all") params.set("location", filters.location);
    if (filters.department !== "all") params.set("department", filters.department);
    if (filters.experience !== "all") params.set("experience", filters.experience);
    params.set("limit", "60");
    setJobs(null);
    axios.get(`${API}/jobs?${params.toString()}`)
      .then((r) => setJobs(r.data))
      .catch((e) => { setError(e.message); setJobs([]); });
  }, [filters]);

  const handleSubmit = (v) => {
    const params = new URLSearchParams();
    if (v.q.trim()) params.set("q", v.q.trim());
    if (v.location !== "all") params.set("location", v.location);
    if (v.department !== "all") params.set("department", v.department);
    if (v.experience !== "all") params.set("experience", v.experience);
    setSp(params, { replace: true });
  };

  const stats = useMemo(() => ({
    count: jobs?.length || 0,
    departments: new Set((jobs || []).map((j) => j.department)).size,
    vacancies: (jobs || []).reduce((s, j) => s + (j.openings || 0), 0),
  }), [jobs]);

  return (
    <>
      <section className="pt-28 lg:pt-36 pb-10 lg:pb-12 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-primary">Careers</p>
          <h1 className="mt-3 font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-[1.05] tracking-tight max-w-4xl mx-auto">
            Find your next opportunity
          </h1>
          <p className="mt-5 max-w-2xl mx-auto text-base sm:text-lg text-slate-600 leading-relaxed">
            Live roles across manufacturing, logistics, retail, IT, hospitality and more.
          </p>
        </div>
      </section>

      <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Careers" }]} />

      <section data-testid="careers-section" className="py-12 lg:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Recruitment Partner banner */}
          <div
            data-testid="careers-partner-banner"
            className="mb-6 rounded-2xl border border-primary/20 bg-gradient-to-r from-primary/5 via-blue-50 to-white p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5"
          >
            <div className="flex items-start sm:items-center gap-3 flex-1">
              <div className="w-10 h-10 shrink-0 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                <Handshake className="w-5 h-5" strokeWidth={1.75} />
              </div>
              <div>
                <div className="font-heading font-bold text-slate-900 text-sm sm:text-base leading-tight">
                  Are you a Recruitment Partner?
                </div>
                <p className="mt-1 text-xs sm:text-sm text-slate-600 leading-relaxed">
                  Login to submit candidates for assigned jobs and track your commissions.
                </p>
              </div>
            </div>
            <Link
              to="/login"
              data-testid="careers-partner-login-cta"
              className="shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-white font-semibold text-sm hover:bg-primary-hover transition-colors"
            >
              Recruitment Partner Login
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <JobSearch value={filters} onChange={setFilters} onSubmit={handleSubmit} variant="bordered" />

          <div className="mt-6 flex flex-wrap items-center gap-3 text-sm text-slate-600">
            <Pill icon={Briefcase} value={stats.count} label="Open roles" />
            <Pill icon={Building2} value={stats.departments} label="Departments" />
            <Pill icon={Users} value={stats.vacancies} label="Total vacancies" />
          </div>

          {/* Results */}
          <div className="mt-10">
            {jobs === null && <div className="py-16 text-center text-slate-500">Loading openings…</div>}
            {jobs && jobs.length === 0 && (
              <div data-testid="careers-empty" className="py-16 text-center bg-slate-50 border border-slate-200 rounded-2xl">
                <p className="font-heading font-bold text-slate-900 text-lg">No matching roles right now.</p>
                <p className="mt-1 text-slate-500 text-sm">Try clearing filters or join our talent pool.</p>
                <Link to="/join" className="mt-5 inline-flex px-5 py-2.5 rounded-lg bg-primary text-white text-sm font-semibold">Join Talent Pool</Link>
              </div>
            )}
            {jobs && jobs.length > 0 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
                {jobs.map((j) => <JobCard key={j.id} job={j} />)}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Talent pool CTA */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl">Don't see the right role?</h2>
          <p className="mt-4 text-slate-300">Join our talent pool — we'll match you to relevant openings as they come up.</p>
          <Link
            to="/join"
            data-testid="careers-join-cta"
            className="mt-8 inline-flex px-7 py-3.5 rounded-lg bg-white text-slate-900 font-semibold hover:bg-blue-50 transition-colors"
          >
            Join our Talent Pool
          </Link>
        </div>
      </section>
    </>
  );
}

function Pill({ icon: Icon, value, label }) {
  return (
    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200">
      <Icon className="w-3.5 h-3.5 text-primary" />
      <span className="font-bold text-slate-900">{value}</span>
      <span className="text-slate-500 text-xs">{label}</span>
    </span>
  );
}
